import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  getPtProgramme, createPtProgramme, updatePtProgramme, copyPtProgramme, archivePtProgramme,
  ptProgrammeMeta, ptProgrammeClient, listExercises, listSessionSchedules,
} from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Trash2, Copy, Save, Archive, Eye, TrendingUp, BookmarkPlus, Trophy, Link2, Pencil, RotateCcw, PlayCircle, ChevronUp, ChevronDown, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
// Each exercise's week-on-week change, applied to every week after week 1 that follows it.
const PROGRESSIONS = [
  { value: "", label: "Same every week", unit: "" },
  { value: "kg", label: "Load", unit: "kg", step: 2.5 },
  { value: "pct", label: "% of 1RM", unit: "%", step: 2.5 },
  { value: "reps", label: "Reps", unit: "reps", step: 1 },
  { value: "sets", label: "Sets", unit: "sets", step: 1 },
  { value: "time", label: "Time", unit: "s", step: 15 },
  { value: "distance", label: "Distance", unit: "m", step: 100 },
];
const FIELD = { kg: "load_kg", pct: "pct_1rm", reps: "reps", sets: "sets", time: "time", distance: "distance_m" };
const BLOCKS = [["warmup", "Warm-up"], ["core", "Core"], ["strength", "Strength"], ["skill", "Skill"], ["metcon", "Metcon"],
  ["accessory", "Supersets"], ["finisher", "Finisher"], ["cooldown", "Cool-down"], ["other", "Other"]];
const SCORES = [["none", "No score"], ["time", "For time"], ["rounds", "Rounds"], ["load", "Load"], ["reps", "Reps"]];
const KIND_LABEL = { group: "Daily workout (whole gym)", PT: "PT (one client)", SPT: "SPT (small group)", template: "Template" };

const uid = () => Math.random().toString(36).slice(2, 12);
const emptyExercise = () => ({ uid: uid(), name: "", sets: 3, reps: "", time: "", distance_m: "", load_kg: "", pct_1rm: "", tempo: "", rest_s: "", superset: "", notes: "",
  block: "", scheme: "", video_url: "", prog_kind: "", prog_step: "", prog_every: "1" });
const emptyDay = (weekday, slot = "") => ({ weekday, slot, title: "", notes: "", score_type: "none", exercises: [emptyExercise()] });
const byDayAndTime = (a, b) => a.weekday - b.weekday || (a.slot || "").localeCompare(b.slot || "");
// "07:00" → "7 am"
export const slotLabel = (t) => {
  if (!t || typeof t !== "string") return "All sessions";
  if (t === "LRC") return "Run club";
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}${m ? `:${String(m).padStart(2, "0")}` : ""} ${h < 12 ? "am" : "pm"}`;
};
const autoWeek = () => ({ label: "", auto: true, days: [] });
const nextMonday = () => {
  const d = new Date();
  d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const nameKey = (s) => (s || "").trim().replace(/\s+/g, " ").toLowerCase();

// "1:30" / "90" → seconds, and back.
const toSeconds = (t) => {
  const v = String(t ?? "").trim();
  if (!v) return null;
  if (!/^\d+(:\d{1,2})?$/.test(v)) return NaN;
  return v.split(":").reduce((a, p) => a * 60 + Number(p), 0);
};
const fromSeconds = (s) => (s == null ? "" : s >= 60 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : String(s));
const num = (v) => (v === "" || v == null ? null : Number(v));
const str = (v) => (v == null ? "" : String(v));

// Server → form (strings for inputs) and form → server.
const toForm = (p) => ({
  ...p,
  notes: p.notes || "",
  slots: p.slots || 4,
  weeks: (p.weeks || []).map((w, wi) => ({
    label: w.label || "", auto: wi > 0 && !!w.auto,
    days: wi > 0 && w.auto ? [] : w.days.map((d) => ({
      ...d, title: d.title || "", notes: d.notes || "", score_type: d.score_type || "none", slot: d.slot || "",
      exercises: d.exercises.map((e) => ({
        uid: e.uid || uid(), name: e.name, sets: e.sets, reps: str(e.reps), time: fromSeconds(e.time_s),
        distance_m: str(e.distance_m), load_kg: str(e.load_kg), pct_1rm: str(e.pct_1rm), tempo: e.tempo || "",
        rest_s: str(e.rest_s), superset: e.superset || "", notes: e.notes || "",
        block: e.block || "", scheme: e.scheme || "", video_url: e.video_url || "",
        prog_kind: e.progression?.kind || "", prog_step: str(e.progression?.step), prog_every: str(e.progression?.every || 1),
      })),
    })),
  })),
  overrides: p.overrides || {},
});

function toBody(f) {
  const weeks = f.weeks.map((w, wi) => ({
    label: w.label.trim() || null, auto: wi > 0 && !!w.auto,
    days: (wi > 0 && w.auto ? [] : w.days).map((d) => ({
      weekday: Number(d.weekday), slot: f.kind === "group" ? d.slot || null : null,
      title: d.title.trim(), notes: d.notes.trim() || null, score_type: d.score_type || "none",
      exercises: d.exercises.filter((e) => e.name.trim()).map((e) => {
        const time = toSeconds(e.time);
        if (Number.isNaN(time)) throw new Error(`Week ${wi + 1} ${WEEKDAYS[d.weekday]}: time for ${e.name} looks like 0:45 or 45`);
        if (e.load_kg !== "" && e.pct_1rm !== "") throw new Error(`${e.name}: give kg or % of 1RM, not both`);
        if (e.prog_kind && !Number(e.prog_step)) throw new Error(`${e.name}: say how much it goes up each time`);
        return {
          uid: e.uid, name: e.name.trim(), sets: Number(e.sets) || 1, reps: num(e.reps), time_s: time,
          distance_m: num(e.distance_m), load_kg: num(e.load_kg), pct_1rm: num(e.pct_1rm), tempo: e.tempo.trim() || null,
          rest_s: num(e.rest_s), superset: e.superset.trim() || null, notes: e.notes.trim() || null,
          block: e.block || null, scheme: e.scheme.trim() || null, video_url: e.video_url.trim() || null,
          progression: e.prog_kind && wi === 0 ? { kind: e.prog_kind, step: Number(e.prog_step), every: Number(e.prog_every) || 1 } : null,
        };
      }),
    })),
  }));
  return {
    kind: f.kind, name: f.name.trim(), notes: f.notes.trim() || null, trainer_user_id: f.trainer_user_id || null,
    client_ids: f.client_ids, start_date: f.start_date || null, weeks, overrides: f.kind === "SPT" ? f.overrides : {},
    slots: f.kind === "SPT" ? Number(f.slots) || 4 : null,
  };
}

// Week `wi` of an auto week: week 1 with each exercise's progression (mirrors routes/programmes.py).
function derivedWeek(base, wi) {
  return base.days.map((d) => ({
    ...d,
    exercises: d.exercises.map((e) => {
      const x = { ...e, uid: uid(), prog_kind: "", prog_step: "", prog_every: "1" };
      const field = FIELD[e.prog_kind];
      if (!field || e[field] === "" || !Number(e.prog_step)) return x;
      const steps = Math.floor(wi / (Number(e.prog_every) || 1));
      if (field === "time") {
        const s0 = toSeconds(e.time);
        if (!Number.isNaN(s0) && s0 != null) x.time = fromSeconds(Math.max(1, Math.round(s0 + Number(e.prog_step) * steps)));
        return x;
      }
      const v = Math.max(field === "load_kg" ? 0 : 1, Number(e[field]) + Number(e.prog_step) * steps);
      x[field] = String(["reps", "sets", "pct_1rm"].includes(field) ? Math.round(v) : Math.round(v * 100) / 100);
      return x;
    }),
  }));
}

const TYPES = [
  { kind: "group", title: "Daily workout", sub: "Large group training. Everyone at the gym sees it as the day's WOD." },
  { kind: "PT", title: "PT", sub: "One client, with their trainer." },
  { kind: "SPT", title: "SPT", sub: "A small group on one plan, with per-person tweaks." },
];
const MEASURES = [["reps", "reps"], ["time", "time"], ["distance", "metres"]];
const LOADS = [["kg", "kg"], ["pct", "% 1RM"], ["none", "no load"]];
const measureOf = (e) => (e.time !== "" ? "time" : e.distance_m !== "" ? "distance" : "reps");
const loadOf = (e) => (e.pct_1rm !== "" ? "pct" : e.load_kg !== "" ? "kg" : e._load || "kg");

// "3 × 10 @ 60 kg" for the cards on the week board.
function prescription(e) {
  const m = measureOf(e);
  const work = m === "time" ? e.time : m === "distance" ? (e.distance_m ? `${e.distance_m} m` : "") : e.reps;
  const load = e.load_kg !== "" ? ` @ ${e.load_kg} kg` : e.pct_1rm !== "" ? ` @ ${e.pct_1rm}%` : "";
  if (e.scheme) return e.scheme;
  return work ? `${Number(e.sets) > 1 ? `${e.sets} × ` : ""}${work}${load}` : "";
}

export default function PtProgrammeBuilder() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";
  const [form, setForm] = useState(null);
  const [server, setServer] = useState(null);      // last loaded detail (progress etc.)
  const [meta, setMeta] = useState({ trainers: [], clients: [], can_edit: false });
  const [library, setLibrary] = useState([]);
  const [times, setTimes] = useState({});          // weekday → the gym's session start times
  const [week, setWeek] = useState(0);
  const [open, setOpen] = useState(null);          // index of the workout being edited in this week
  const [saving, setSaving] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [clientView, setClientView] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, lib, sched] = await Promise.all([ptProgrammeMeta(), listExercises().catch(() => ({ exercises: [] })),
        listSessionSchedules().catch(() => [])]);
      setMeta(m);
      setLibrary(lib.exercises || []);
      const t = {};
      for (const s of sched.filter((x) => x.active !== false)) (t[s.day_of_week] = t[s.day_of_week] || []).push(s.start_time);
      Object.values(t).forEach((v) => v.sort());
      setTimes(t);
      setWeek(0);
      setOpen(null);
      if (isNew) {
        const kind = params.get("kind") || "";
        setForm({ kind, name: "", notes: "", slots: 4, trainer_user_id: "", client_ids: [], start_date: kind === "template" ? "" : nextMonday(),
                  weeks: [{ label: "", auto: false, days: [] }, autoWeek(), autoWeek(), autoWeek()], overrides: {} });
      } else {
        const d = await getPtProgramme(id);
        setServer(d);
        setForm(toForm(d.programme));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load the programme");
    }
  }, [id, isNew, params]);
  useEffect(() => { load(); }, [load]);

  const libByKey = useMemo(() => {
    const m = new Map();
    for (const e of library) for (const k of e.keys || [nameKey(e.name)]) m.set(k, e);
    return m;
  }, [library]);

  const canEdit = meta.can_edit;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setWeekAt = (wi, patch) => setForm((f) => ({ ...f, weeks: f.weeks.map((w, j) => (j === wi ? { ...w, ...patch } : w)) }));

  const save = async () => {
    let body;
    try {
      body = toBody(form);
    } catch (e) {
      toast.error(e.message);
      return;
    }
    if (body.name.length < 2) { toast.error("Give the programme a name"); return; }
    setSaving(true);
    try {
      const r = isNew ? await createPtProgramme(body) : await updatePtProgramme(id, body);
      toast.success(body.kind === "group" ? "Saved and published as the gym's daily workouts" : isNew ? "Programme created" : "Saved");
      if (isNew) navigate(`/member-app/programmes/${r.programme.id}`, { replace: true });
      else load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg?.replace("Value error, ", "") : d || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    if (!window.confirm(form.kind === "group"
      ? `Archive ${form.name}? Its workouts from today on come off the gym's calendar; past logs are kept.`
      : `Archive ${form.name}? Clients stop seeing it; their logs are kept.`)) return;
    try {
      await archivePtProgramme(id);
      toast.success("Archived");
      navigate("/member-app/programmes");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't archive");
    }
  };

  const saveAsTemplate = async () => {
    try {
      const r = await copyPtProgramme(id, { kind: "template", name: `${form.name} (template)` });
      toast.success("Saved as a template");
      navigate(`/member-app/programmes/${r.programme.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the template");
    }
  };

  const exerciseNames = useMemo(() => {
    const names = new Map();
    for (const w of form?.weeks || []) for (const d of w.days) for (const e of d.exercises) if (e.name.trim()) names.set(nameKey(e.name), e.name.trim());
    return [...names.values()];
  }, [form]);

  if (!form) return <div className="card-surface p-8 text-center text-sm text-zinc-500">Loading…</div>;

  // Step 1 for a new programme: what are you building?
  if (!form.kind) {
    return (
      <div className="space-y-6" data-testid="programme-builder">
        <BackLink navigate={navigate} />
        <h2 className="font-display text-3xl font-extrabold tracking-tight">What are you building?</h2>
        <div className="grid gap-3 md:grid-cols-3">
          {TYPES.map((t) => (
            <button key={t.kind} type="button" data-testid={`programme-type-${t.kind}`}
              onClick={() => set({ kind: t.kind, weeks: form.weeks.map((w, i) => (i === 0 ? { ...w, days: [] } : w)) })}
              className="card-surface p-5 text-left transition hover:border-emerald-500/60">
              <div className="text-lg font-bold text-zinc-100">{t.title}</div>
              <div className="mt-1 text-sm text-zinc-400">{t.sub}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const wi = form.weeks[week] ? week : 0;
  const isAuto = wi > 0 && form.weeks[wi].auto;
  const days = isAuto ? derivedWeek(form.weeks[0], wi) : form.weeks[wi].days;
  const isTemplate = form.kind === "template";
  const isGroup = form.kind === "group";
  const editable = canEdit && !isAuto;

  const setDays = (next) => setWeekAt(wi, { days: [...next].sort(byDayAndTime) });
  const setWeeksCount = (n) => {
    const count = Math.max(1, Math.min(52, Number(n) || 1));
    const weeks = form.weeks.slice(0, count);
    while (weeks.length < count) weeks.push(autoWeek());
    set({ weeks });
    if (week >= count) setWeek(count - 1);
  };
  const addWorkout = (weekday) => {
    const taken = new Set(days.filter((d) => d.weekday === weekday).map((d) => d.slot || ""));
    let slot = "";
    let current = days;
    if (isGroup && taken.size) {
      slot = (times[weekday] || []).find((t) => !taken.has(t)) || "";
      if (taken.has("")) {      // the first one was "all sessions": give it the first class time
        const first = (times[weekday] || [])[0] || "07:00";
        current = days.map((d) => (d.weekday === weekday && !d.slot ? { ...d, slot: first } : d));
        slot = (times[weekday] || []).find((t) => t !== first && !taken.has(t)) || "";
      }
    }
    const fresh = { ...emptyDay(weekday, slot), exercises: [emptyExercise()] };
    const next = [...current, fresh].sort(byDayAndTime);
    setDays(next);
    setOpen(next.indexOf(fresh));
  };
  const copyTo = (src, weekdays) => {
    let next = [...days];
    for (const wd of weekdays) {
      next = next.filter((d) => !(d.weekday === wd && (d.slot || "") === (src.slot || "")));
      next.push({ ...src, weekday: wd, exercises: src.exercises.map((e) => ({ ...e, uid: uid() })) });
    }
    setDays(next);
    toast.success(`Copied to ${weekdays.map((w) => WEEKDAYS[w]).join(", ")}`);
  };

  return (
    <div className="space-y-6" data-testid="programme-builder">
      <datalist id="exercise-library">{library.map((e) => <option key={e.id} value={e.name} />)}</datalist>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0 flex-1">
          <BackLink navigate={navigate} />
          <div className="eyebrow">
            {KIND_LABEL[form.kind]}
            {isNew && !isTemplate && (
              <button type="button" onClick={() => set({ kind: "", client_ids: [] })} className="ml-2 normal-case tracking-normal text-zinc-500 underline hover:text-zinc-300">change</button>
            )}
          </div>
          <input value={form.name} onChange={(e) => set({ name: e.target.value })} disabled={!canEdit} data-testid="programme-name"
            placeholder={isGroup ? "Name it, e.g. October strength block" : isTemplate ? "Name it, e.g. Beginner strength, 3×/week" : "Name it, e.g. Asha – strength block"}
            className="mt-1 w-full bg-transparent font-display text-3xl font-extrabold tracking-tight text-zinc-100 placeholder:text-zinc-600 focus:outline-none" />
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            {!isNew && isTemplate && (
              <Button variant="outline" onClick={() => setAssigning(true)} data-testid="programme-assign"><Copy className="mr-1 h-4 w-4" /> Assign</Button>
            )}
            {!isNew && !isTemplate && (
              <Button variant="outline" onClick={saveAsTemplate} data-testid="programme-save-template"><BookmarkPlus className="mr-1 h-4 w-4" /> Save as template</Button>
            )}
            {!isNew && <Button variant="outline" onClick={archive}><Archive className="mr-1 h-4 w-4" /> Archive</Button>}
            <Button onClick={save} disabled={saving} data-testid="programme-save"><Save className="mr-1 h-4 w-4" /> {saving ? "Saving…" : isGroup ? "Save & publish" : "Save"}</Button>
          </div>
        )}
      </div>

      {!canEdit && <div className="flex items-center gap-2 text-xs text-zinc-500"><Eye className="h-3.5 w-3.5" /> View only. The programming team writes programmes.</div>}

      {server?.progress && Object.keys(server.progress).length > 0 && (
        <div className="card-surface p-4" data-testid="programme-progress">
          <div className="eyebrow mb-2">Planned against done</div>
          <div className="flex flex-wrap gap-3">
            {form.client_ids.map((cid) => {
              const p = server.progress[cid] || {};
              const c = server.summary.clients.find((x) => x.id === cid);
              return (
                <button key={cid} type="button" onClick={() => setClientView(cid)}
                  className="rounded-lg border border-zinc-800 px-4 py-2 text-left hover:border-zinc-600">
                  <div className="text-sm font-semibold text-zinc-100">{c?.name || "Client"}</div>
                  <div className="text-xs text-zinc-400 tabular-nums">{p.done ?? 0} of {p.planned ?? 0} sessions · last {p.last_done || "never"}</div>
                  {p.prs > 0 && <div className="mt-0.5 flex items-center gap-1 text-[11px] text-amber-300"><Trophy className="h-3 w-3" /> {p.prs} PR{p.prs === 1 ? "" : "s"}</div>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <fieldset disabled={!canEdit} className="min-w-0 space-y-6">
        {/* Who and when, in one row */}
        <div className="card-surface flex flex-wrap items-end gap-x-6 gap-y-4 p-5">
          {!isTemplate && (
            <Field label="Starts">
              <Input type="date" value={form.start_date || ""} onChange={(e) => set({ start_date: e.target.value })} className="w-44" />
            </Field>
          )}
          <Field label="Weeks">
            <div className="flex items-center gap-1">
              <Button size="icon" variant="outline" type="button" className="h-9 w-9" onClick={() => setWeeksCount(form.weeks.length - 1)}>−</Button>
              <Input type="number" min={1} max={52} value={form.weeks.length} onChange={(e) => setWeeksCount(e.target.value)}
                className="h-9 w-14 text-center" data-testid="programme-weeks-count" />
              <Button size="icon" variant="outline" type="button" className="h-9 w-9" onClick={() => setWeeksCount(form.weeks.length + 1)}>+</Button>
            </div>
          </Field>
          {isGroup && <div className="pb-2 text-sm text-zinc-400">For <span className="text-zinc-200">everyone at the gym</span>: each workout shows in the app as that day&apos;s WOD.</div>}
          {!isTemplate && !isGroup && (
            <Field label="Trainer">
              <select className="h-9 w-56 rounded-md border border-zinc-700 bg-transparent px-3 text-sm" value={form.trainer_user_id || ""}
                onChange={(e) => set({ trainer_user_id: e.target.value })} data-testid="programme-trainer">
                <option value="">Pick a trainer</option>
                {meta.trainers.map((t) => <option key={t.user_id} value={t.user_id}>{t.name}</option>)}
              </select>
            </Field>
          )}
          {form.kind === "SPT" && (
            <Field label="Places">
              <Input type="number" min={1} max={12} value={form.slots ?? 4} onChange={(e) => set({ slots: e.target.value })}
                className="h-9 w-20 text-center" data-testid="programme-slots" />
            </Field>
          )}
          {!isTemplate && !isGroup && (
            <div className="min-w-[280px] flex-1">
              <ClientPicker kind={form.kind} clients={meta.clients} value={form.client_ids} onChange={(client_ids) => set({ client_ids })} />
            </div>
          )}
          {form.kind === "SPT" ? (
            <div className="w-full space-y-1">
              <span className="text-xs font-medium text-zinc-400">About this programme</span>
              <Textarea rows={3} value={form.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="programme-about"
                placeholder="What it's for, who it suits and what members get. Members see this on the semi-private training page in the app." />
              <p className="text-[11px] text-zinc-500">
                The app shows this group with its start date, days and times, trainer and places left
                ({Math.max(0, (Number(form.slots) || 4) - form.client_ids.length)} of {Number(form.slots) || 4} now). You can save it before anyone joins.
              </p>
            </div>
          ) : (
            <div className="w-full">
              <Input value={form.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="A note members see with the programme (optional)" />
            </div>
          )}
        </div>

        {/* Weeks */}
        <div className="flex flex-wrap items-center gap-2" data-testid="programme-weeks">
          {form.weeks.map((wk, i) => (
            <button key={i} type="button" onClick={() => { setWeek(i); setOpen(null); }}
              className={`rounded-full border px-3 py-1 text-xs ${i === wi ? "border-emerald-500/60 bg-emerald-500/10 text-zinc-100" : "border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}>
              Week {i + 1}{wk.label ? ` · ${wk.label}` : ""}{i > 0 && !wk.auto ? " ✎" : ""}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-400">
          {wi === 0 ? (
            <span><TrendingUp className="mr-1 inline h-4 w-4 text-emerald-400" />Build week 1. Set how each exercise goes up, and the other weeks fill in.</span>
          ) : isAuto ? (
            <>
              <span>Week {wi + 1} is week 1 with each exercise&apos;s weekly increase.</span>
              {canEdit && (
                <Button size="sm" variant="outline" type="button" data-testid="programme-customise-week"
                  onClick={() => setWeekAt(wi, { auto: false, days: derivedWeek(form.weeks[0], wi) })}>
                  <Pencil className="mr-1 h-3.5 w-3.5" /> Change this week (e.g. deload)
                </Button>
              )}
            </>
          ) : (
            <>
              <span>Week {wi + 1} is written by hand.</span>
              {canEdit && (
                <Button size="sm" variant="ghost" type="button" onClick={() => setWeekAt(wi, { auto: true, days: [] })}>
                  <RotateCcw className="mr-1 h-3.5 w-3.5" /> Go back to following week 1
                </Button>
              )}
            </>
          )}
          {wi > 0 && <Input value={form.weeks[wi].label} onChange={(e) => setWeekAt(wi, { label: e.target.value })} placeholder="Name this week (optional)" className="h-8 max-w-[220px]" />}
        </div>

        {/* The week board: one column per day, a card per workout */}
        <div className="grid gap-2 md:grid-cols-7" data-testid="week-board">
          {WEEKDAYS.map((name, wd) => {
            const mine = days.map((d, i) => ({ d, i })).filter(({ d }) => Number(d.weekday) === wd);
            const canAdd = editable && (isGroup || mine.length === 0);
            return (
              <div key={wd} className="flex min-h-[160px] flex-col gap-2 rounded-xl border border-zinc-800/80 bg-zinc-950/40 p-2">
                <div className="px-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{name}</div>
                {mine.map(({ d, i }) => (
                  <button key={i} type="button" onClick={() => setOpen(i)} data-testid={`workout-card-${wd}`}
                    className="rounded-lg border border-zinc-800 bg-zinc-900 p-2 text-left transition hover:border-emerald-500/50">
                    {isGroup && <div className="text-[11px] font-semibold text-emerald-300">{slotLabel(d.slot)}</div>}
                    <div className="truncate text-sm font-semibold text-zinc-100">{d.title || "Untitled workout"}</div>
                    <div className="mt-1 space-y-0.5">
                      {d.exercises.filter((e) => e.name).slice(0, 4).map((e) => (
                        <div key={e.uid} className="truncate text-[11px] text-zinc-400">{e.name} <span className="text-zinc-500">{prescription(e)}</span></div>
                      ))}
                      {d.exercises.filter((e) => e.name).length > 4 && <div className="text-[11px] text-zinc-500">+{d.exercises.filter((e) => e.name).length - 4} more</div>}
                      {!d.exercises.some((e) => e.name) && <div className="text-[11px] text-amber-300/80">No exercises yet</div>}
                    </div>
                  </button>
                ))}
                {canAdd && (
                  <button type="button" onClick={() => addWorkout(wd)} data-testid={`add-workout-${wd}`}
                    className="flex items-center justify-center gap-1 rounded-lg border border-dashed border-zinc-700 py-2 text-xs text-zinc-500 hover:border-zinc-500 hover:text-zinc-300">
                    <Plus className="h-3.5 w-3.5" /> {mine.length ? "Another class" : "Add workout"}
                  </button>
                )}
                {!mine.length && !canAdd && <div className="px-1 text-[11px] text-zinc-600">Rest</div>}
              </div>
            );
          })}
        </div>

        {form.kind === "SPT" && form.client_ids.length > 0 && (
          <Overrides clients={meta.clients.filter((c) => form.client_ids.includes(c.id))} names={exerciseNames}
            value={form.overrides} onChange={(overrides) => set({ overrides })} />
        )}
      </fieldset>

      {open !== null && days[open] && (
        <WorkoutSheet
          day={days[open]} group={isGroup} times={times} libByKey={libByKey} readOnly={!editable} firstWeek={wi === 0}
          onClose={() => setOpen(null)}
          onChange={(patch) => setDays(days.map((d, j) => (j === open ? { ...d, ...patch } : d)))}
          onRemove={() => { setDays(days.filter((_, j) => j !== open)); setOpen(null); }}
          onCopy={(weekdays) => copyTo(days[open], weekdays)}
          onCustomise={isAuto && canEdit ? () => setWeekAt(wi, { auto: false, days: derivedWeek(form.weeks[0], wi) }) : null}
        />
      )}

      {assigning && <AssignDialog source={{ id, name: form.name }} onClose={() => setAssigning(false)} onDone={(p) => navigate(`/member-app/programmes/${p.id}`)} />}
      {clientView && <ClientProgressDialog programmeId={id} clientId={clientView} onClose={() => setClientView(null)} />}
    </div>
  );
}

function BackLink({ navigate }) {
  return (
    <button type="button" onClick={() => navigate("/member-app/programmes")} className="mb-1 flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300">
      <ArrowLeft className="h-3.5 w-3.5" /> All programmes
    </button>
  );
}

/** One workout, edited in a side panel: title, time, then its exercises as cards. */
function WorkoutSheet({ day, group, times, libByKey, readOnly, firstWeek, onClose, onChange, onRemove, onCopy, onCustomise }) {
  const [copying, setCopying] = useState(false);
  const [copyDays, setCopyDays] = useState([]);
  const exs = day.exercises;
  const setEx = (i, patch) => onChange({ exercises: exs.map((e, j) => (j === i ? { ...e, ...patch } : e)) });
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= exs.length) return;
    const next = [...exs];
    [next[i], next[j]] = [next[j], next[i]];
    onChange({ exercises: next });
  };
  const add = (block) => {
    const e = { ...emptyExercise(), block: block || "" };
    if (!block) { onChange({ exercises: [...exs, e] }); return; }
    // keep each section together: insert after the last exercise of that section
    const order = BLOCKS.map(([k]) => k);
    let at = exs.length;
    for (let i = exs.length - 1; i >= 0; i--) {
      if (order.indexOf(exs[i].block || "strength") <= order.indexOf(block)) { at = i + 1; break; }
      at = i;
    }
    onChange({ exercises: [...exs.slice(0, at), e, ...exs.slice(at)] });
  };
  const sections = group
    ? BLOCKS.filter(([k]) => ["warmup", "strength", "metcon"].includes(k) || exs.some((e) => (e.block || "strength") === k))
    : [[null, null]];

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto border-zinc-800 bg-[#0c0c0c] sm:max-w-2xl" data-testid="workout-sheet">
        <SheetHeader>
          <SheetTitle className="text-zinc-100">{WEEKDAYS[day.weekday]}{group ? ` · ${slotLabel(day.slot)}` : ""}</SheetTitle>
          <SheetDescription>{readOnly ? "This week follows week 1. Change it by hand to edit." : "Changes are kept when you close this. Save the programme to publish."}</SheetDescription>
        </SheetHeader>

        {onCustomise && (
          <Button size="sm" variant="outline" className="mt-3" onClick={onCustomise}><Pencil className="mr-1 h-3.5 w-3.5" /> Change this week by hand</Button>
        )}

        <fieldset disabled={readOnly} className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-2">
            <Input value={day.title} onChange={(e) => onChange({ title: e.target.value })} data-testid="workout-title"
              placeholder={group ? "Workout name, e.g. Engine day" : "Session name, e.g. Lower body"} className="min-w-[200px] flex-1" />
            {group && <SlotPicker value={day.slot || ""} times={times[day.weekday]} onChange={(slot) => onChange({ slot })} />}
            {group && (
              <select className="h-9 rounded-md border border-zinc-700 bg-transparent px-2 text-xs" value={day.score_type || "none"} onChange={(e) => onChange({ score_type: e.target.value })}>
                {SCORES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            )}
          </div>
          <Textarea rows={2} value={day.notes} onChange={(e) => onChange({ notes: e.target.value })} placeholder="Notes for members (optional)" />

          {sections.map(([block, label]) => {
            const rows = exs.map((e, i) => ({ e, i })).filter(({ e }) => !group || (e.block || "strength") === block);
            return (
              <div key={block || "all"} className="space-y-2">
                {group && <div className="eyebrow">{label}</div>}
                {rows.map(({ e, i }) => (
                  <ExerciseCard key={e.uid} e={e} n={i + 1} lib={libByKey.get(nameKey(e.name))} readOnly={readOnly} firstWeek={firstWeek} group={group}
                    onChange={(patch) => setEx(i, patch)} onUp={() => move(i, -1)} onDown={() => move(i, 1)}
                    onRemove={() => onChange({ exercises: exs.filter((_, j) => j !== i) })} />
                ))}
                {!readOnly && (
                  <button type="button" onClick={() => add(block)} data-testid={`add-exercise-${block || "all"}`}
                    className="flex w-full items-center justify-center gap-1 rounded-lg border border-dashed border-zinc-700 py-2 text-xs text-zinc-400 hover:border-zinc-500 hover:text-zinc-200">
                    <Plus className="h-3.5 w-3.5" /> Add {group ? label.toLowerCase() : "an"} exercise
                  </button>
                )}
              </div>
            );
          })}
          {group && !readOnly && (
            <select className="h-8 rounded-md border border-zinc-800 bg-transparent px-2 text-xs text-zinc-400" value=""
              onChange={(e) => e.target.value && add(e.target.value)}>
              <option value="">+ Another section…</option>
              {BLOCKS.filter(([k]) => !sections.some(([s]) => s === k)).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          )}
        </fieldset>

        {!readOnly && (
          <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-zinc-800 pt-4">
            {!copying ? (
              <Button size="sm" variant="outline" onClick={() => setCopying(true)}><Copy className="mr-1 h-3.5 w-3.5" /> Copy to other days</Button>
            ) : (
              <div className="flex flex-wrap items-center gap-1">
                {WEEKDAYS.map((n, i) => i !== day.weekday && (
                  <button key={n} type="button" onClick={() => setCopyDays((c) => (c.includes(i) ? c.filter((x) => x !== i) : [...c, i]))}
                    className={`rounded-md border px-2 py-1 text-xs ${copyDays.includes(i) ? "border-emerald-500/60 text-zinc-100" : "border-zinc-800 text-zinc-400"}`}>{n}</button>
                ))}
                <Button size="sm" disabled={!copyDays.length} onClick={() => { onCopy(copyDays); setCopying(false); setCopyDays([]); }}>Copy</Button>
              </div>
            )}
            <div className="flex-1" />
            <Button size="sm" variant="ghost" className="text-red-300" onClick={() => { if (window.confirm("Delete this workout?")) onRemove(); }}>
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete workout
            </Button>
            <Button size="sm" onClick={onClose} data-testid="workout-done">Done</Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/** One exercise: name (from the library), a plain "3 × 10 @ 60 kg" line, the weekly increase,
 *  and everything else under "More". */
function ExerciseCard({ e, n, lib, readOnly, firstWeek, group, onChange, onUp, onDown, onRemove }) {
  const [more, setMore] = useState(Boolean(e.tempo || e.rest_s || e.notes || e.superset || e.scheme));
  const [video, setVideo] = useState(false);
  const measure = measureOf(e);
  const loadKind = loadOf(e);
  const prog = PROGRESSIONS.find((p) => p.value === e.prog_kind) || PROGRESSIONS[0];
  const box = "h-9 bg-[#0b0b0b] border-[#262626] text-sm";
  const hasVideo = Boolean(e.video_url || lib?.video_url);
  const setMeasure = (m) => {
    const v = measure === "time" ? e.time : measure === "distance" ? e.distance_m : e.reps;
    onChange({ reps: m === "reps" ? v : "", time: m === "time" ? v : "", distance_m: m === "distance" ? v : "" });
  };
  const setLoadKind = (k) => {
    const v = e.load_kg !== "" ? e.load_kg : e.pct_1rm;
    onChange({ _load: k, load_kg: k === "kg" ? v : "", pct_1rm: k === "pct" ? v : "" });
  };
  const measureField = measure === "time" ? "time" : measure === "distance" ? "distance_m" : "reps";
  const progOptions = PROGRESSIONS.filter((p) => !p.value
    || (p.value === "kg" && loadKind === "kg") || (p.value === "pct" && loadKind === "pct")
    || (p.value === "reps" && measure === "reps") || (p.value === "time" && measure === "time")
    || (p.value === "distance" && measure === "distance") || p.value === "sets");

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3" data-testid="exercise-card">
      <div className="flex items-center gap-2">
        <span className="w-5 text-center text-xs text-zinc-500">{n}</span>
        <Input list="exercise-library" value={e.name} onChange={(ev) => onChange({ name: ev.target.value })} placeholder="Exercise, e.g. Back squat"
          className={`${box} flex-1 font-semibold`} data-testid="exercise-name" />
        {e.name && (
          <button type="button" onClick={() => setVideo(!video)} title={hasVideo ? "Demo video set" : "Add a demo video"}
            className={`flex items-center gap-1 rounded-md px-2 py-1 text-[11px] ${hasVideo ? "text-emerald-300" : "text-zinc-500 hover:text-zinc-300"}`}>
            {hasVideo ? <PlayCircle className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}{hasVideo ? "Video" : "Add video"}
          </button>
        )}
        {!readOnly && (
          <div className="flex">
            <Button size="icon" variant="ghost" type="button" className="h-8 w-7" onClick={onUp} title="Move up"><ChevronUp className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" type="button" className="h-8 w-7" onClick={onDown} title="Move down"><ChevronDown className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" type="button" className="h-8 w-7" onClick={onRemove} title="Remove"><X className="h-4 w-4" /></Button>
          </div>
        )}
      </div>

      {video && (
        <div className="ml-7 mt-2">
          <Input value={e.video_url || lib?.video_url || ""} onChange={(ev) => onChange({ video_url: ev.target.value })}
            placeholder="Paste a YouTube link. It's saved to the exercise library for everyone." className={box} />
        </div>
      )}

      <div className="ml-7 mt-2 flex flex-wrap items-center gap-2 text-sm text-zinc-400">
        <Input type="number" min={1} max={20} value={e.sets} onChange={(ev) => onChange({ sets: ev.target.value })} className={`${box} w-14 text-center`} aria-label="Sets" />
        <span>sets ×</span>
        <Input value={e[measureField]} onChange={(ev) => onChange({ [measureField]: ev.target.value })} aria-label="Amount"
          placeholder={measure === "time" ? "0:45" : measure === "distance" ? "400" : "10"} className={`${box} w-16 text-center`} />
        <select className="h-9 rounded-md border border-[#262626] bg-[#0b0b0b] px-2 text-sm" value={measure} onChange={(ev) => setMeasure(ev.target.value)}>
          {MEASURES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        {loadKind !== "none" && <span>@</span>}
        {loadKind !== "none" && (
          <Input type="number" min={0} step="0.5" value={loadKind === "pct" ? e.pct_1rm : e.load_kg} aria-label="Load"
            onChange={(ev) => onChange(loadKind === "pct" ? { pct_1rm: ev.target.value } : { load_kg: ev.target.value })} className={`${box} w-16 text-center`} />
        )}
        <select className="h-9 rounded-md border border-[#262626] bg-[#0b0b0b] px-2 text-sm" value={loadKind} onChange={(ev) => setLoadKind(ev.target.value)}>
          {LOADS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>

      {firstWeek && (
        <div className="ml-7 mt-2 flex flex-wrap items-center gap-2 text-xs text-zinc-400">
          <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />
          <span>Each week:</span>
          <select className="h-8 rounded-md border border-[#262626] bg-[#0b0b0b] px-2 text-xs" value={e.prog_kind}
            onChange={(ev) => { const p = PROGRESSIONS.find((x) => x.value === ev.target.value); onChange({ prog_kind: ev.target.value, prog_step: p?.step ? String(p.step) : "", prog_every: "1" }); }}>
            {progOptions.map((p) => <option key={p.value} value={p.value}>{p.value ? `add ${p.label.toLowerCase()}` : "keep the same"}</option>)}
          </select>
          {e.prog_kind && (
            <>
              <span>+</span>
              <Input type="number" step="0.5" value={e.prog_step} onChange={(ev) => onChange({ prog_step: ev.target.value })} className="h-8 w-16 bg-[#0b0b0b] border-[#262626] text-center text-xs" />
              <span>{prog.unit}</span>
              <select className="h-8 rounded-md border border-[#262626] bg-[#0b0b0b] px-2 text-xs" value={e.prog_every} onChange={(ev) => onChange({ prog_every: ev.target.value })}>
                {[1, 2, 3, 4].map((k) => <option key={k} value={k}>{k === 1 ? "every week" : `every ${k} weeks`}</option>)}
              </select>
            </>
          )}
        </div>
      )}

      <div className="ml-7 mt-2">
        <button type="button" onClick={() => setMore(!more)} className="text-[11px] text-zinc-500 hover:text-zinc-300">
          {more ? "Less" : "More: rest, tempo, notes"}{group ? ", written scheme" : ", superset"}
        </button>
        {more && (
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <Input type="number" min={0} value={e.rest_s} onChange={(ev) => onChange({ rest_s: ev.target.value })} placeholder="Rest (seconds)" className={box} />
            <Input value={e.tempo} onChange={(ev) => onChange({ tempo: ev.target.value })} placeholder="Tempo, e.g. 3-1-1-0" className={box} />
            {group
              ? <Input value={e.scheme} onChange={(ev) => onChange({ scheme: ev.target.value })} placeholder="Or write it, e.g. AMRAP 12 min" className={box} />
              : <Input value={e.superset} maxLength={2} onChange={(ev) => onChange({ superset: ev.target.value.toUpperCase() })} placeholder="Superset letter, e.g. A" className={box} />}
            <Input value={e.notes} onChange={(ev) => onChange({ notes: ev.target.value })} placeholder="Coaching note" className={box} />
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-zinc-400">{label}</span>
      {children}
      {hint && <span className="block text-xs text-zinc-500">{hint}</span>}
    </label>
  );
}

function ClientPicker({ kind, clients, value, onChange }) {
  const [q, setQ] = useState("");
  const shown = clients.filter((c) => !q || `${c.name} ${c.phone || ""}`.toLowerCase().includes(q.toLowerCase())).slice(0, 40);
  const toggle = (id) => {
    if (kind === "PT") onChange(value.includes(id) ? [] : [id]);
    else onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id].slice(0, 12));
  };
  const picked = clients.filter((c) => value.includes(c.id));
  return (
    <div className="space-y-2">
      <span className="text-xs font-medium text-zinc-400">{kind === "PT" ? "Client" : "Clients in the group (add them as they sign up)"}</span>
      <div className="flex flex-wrap gap-2">
        {picked.map((c) => (
          <span key={c.id} className="flex items-center gap-1 rounded-full bg-emerald-500/10 px-3 py-1 text-xs text-emerald-200">
            {c.name}<button type="button" onClick={() => toggle(c.id)} className="ml-1 text-emerald-300/70 hover:text-emerald-100">×</button>
          </span>
        ))}
        {!picked.length && <span className="text-xs text-zinc-500">No one picked yet. Clients must have joined the app.</span>}
      </div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a client by name or phone" className="max-w-sm" />
      {q && (
        <div className="flex max-w-xl flex-wrap gap-2">
          {shown.map((c) => (
            <button key={c.id} type="button" onClick={() => { toggle(c.id); setQ(""); }}
              className={`rounded-full border px-3 py-1 text-xs ${value.includes(c.id) ? "border-emerald-500/60 text-emerald-200" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>
              {c.name}{c.tag ? <span className="ml-1 text-[10px] text-zinc-500">{c.tag}</span> : null}
            </button>
          ))}
          {!shown.length && <span className="text-xs text-zinc-500">No match.</span>}
        </div>
      )}
    </div>
  );
}

function SlotPicker({ value, times, disabled, onChange }) {
  const [other, setOther] = useState(false);
  const opts = [...new Set([...(times || []), ...(value ? [value] : [])])].sort();     // "LRC" = the run club
  if (other) {
    return <Input type="time" autoFocus value={value} disabled={disabled} className="h-9 w-32"
      onChange={(e) => onChange(e.target.value)} onBlur={() => setOther(false)} />;
  }
  return (
    <select disabled={disabled} className="h-9 rounded-md border border-zinc-700 bg-transparent px-2 text-xs" value={value} title="Which class this workout is for"
      onChange={(e) => (e.target.value === "__other" ? setOther(true) : onChange(e.target.value))}>
      <option value="">All sessions</option>
      {opts.filter((t) => t !== "LRC").map((t) => <option key={t} value={t}>{slotLabel(t)}</option>)}
      <option value="LRC">Run club</option>
      <option value="__other">Other time…</option>
    </select>
  );
}

function Overrides({ clients, names, value, onChange }) {
  const setRow = (cid, key, patch) => {
    const per = { ...(value[cid] || {}) };
    const row = { ...(per[key] || {}), ...patch };
    if (!row.swap && !row.load_pct) delete per[key]; else per[key] = row;
    onChange({ ...value, [cid]: per });
  };
  return (
    <div className="card-surface p-5" data-testid="programme-overrides">
      <div className="eyebrow">Per-client adjustments</div>
      <p className="mt-1 mb-3 text-xs text-zinc-500">One plan for the group. Swap an exercise for someone, or scale their loads (e.g. 80% of the written kg). Applies to every week.</p>
      <div className="space-y-4">
        {clients.map((c) => (
          <div key={c.id}>
            <div className="mb-1 text-sm font-semibold text-zinc-200">{c.name}</div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {names.map((n) => {
                const row = (value[c.id] || {})[nameKey(n)] || {};
                return (
                  <div key={n} className="flex items-center gap-2 rounded-md border border-zinc-800 px-2 py-1.5 text-xs">
                    <span className="w-28 truncate text-zinc-400" title={n}>{n}</span>
                    <Input list="exercise-library" value={row.swap || ""} onChange={(e) => setRow(c.id, nameKey(n), { swap: e.target.value || undefined })}
                      placeholder="Swap for…" className="h-7 flex-1 bg-[#0b0b0b] border-[#262626] px-2 text-xs" />
                    <Input type="number" min={10} max={200} value={row.load_pct || ""} onChange={(e) => setRow(c.id, nameKey(n), { load_pct: e.target.value ? Number(e.target.value) : undefined })}
                      placeholder="%" className="h-7 w-14 bg-[#0b0b0b] border-[#262626] px-2 text-xs" title="% of the written load" />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AssignDialog({ source, onClose, onDone }) {
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({ kind: "PT", name: source.name, client_ids: [], trainer_user_id: "", start_date: nextMonday() });
  const [saving, setSaving] = useState(false);
  useEffect(() => { ptProgrammeMeta().then(setMeta).catch(() => setMeta({ trainers: [], clients: [] })); }, []);
  const go = async () => {
    setSaving(true);
    try {
      const r = await copyPtProgramme(source.id, form);
      toast.success("Assigned. It shows in the client's app from the start date.");
      onDone(r.programme);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't assign");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign {source.name}</DialogTitle>
          <DialogDescription>Makes a copy for the client or group. Edits to the copy don't change the template.</DialogDescription>
        </DialogHeader>
        {!meta ? <div className="text-sm text-zinc-500">Loading…</div> : (
          <div className="space-y-3">
            <div className="flex gap-2">
              {["group", "PT", "SPT"].map((k) => (
                <button key={k} type="button" onClick={() => setForm({ ...form, kind: k, client_ids: k === "group" ? [] : form.client_ids.slice(0, k === "PT" ? 1 : 12) })}
                  className={`rounded-md border px-3 py-1.5 text-xs ${form.kind === k ? "border-emerald-500/60 text-zinc-100" : "border-zinc-800 text-zinc-400"}`}>
                  {KIND_LABEL[k]}
                </button>
              ))}
            </div>
            <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            {form.kind === "group"
              ? <div className="text-xs text-zinc-500">For everyone at this gym: each day becomes that day&apos;s WOD.</div>
              : <ClientPicker kind={form.kind} clients={meta.clients} value={form.client_ids} onChange={(client_ids) => setForm({ ...form, client_ids })} />}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Trainer">
                <select className="w-full rounded-md border border-zinc-700 bg-transparent px-3 py-2 text-sm" value={form.trainer_user_id}
                  onChange={(e) => setForm({ ...form, trainer_user_id: e.target.value })}>
                  <option value="">Pick a trainer</option>
                  {meta.trainers.map((t) => <option key={t.user_id} value={t.user_id}>{t.name}</option>)}
                </select>
              </Field>
              <Field label="Start date"><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={go} disabled={saving || (form.kind === "PT" && !form.client_ids.length)} data-testid="assign-confirm">{saving ? "Assigning…" : "Assign"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ClientProgressDialog({ programmeId, clientId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { ptProgrammeClient(programmeId, clientId).then(setData).catch(() => toast.error("Couldn't load progress")); }, [programmeId, clientId]);
  const today = new Date().toISOString().slice(0, 10);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{data?.client?.name || "Client"}: planned against done</DialogTitle>
          <DialogDescription>{data ? `${data.progress.done} of ${data.progress.planned} sessions so far · last ${data.progress.last_done || "never"}` : "Loading…"}</DialogDescription>
        </DialogHeader>
        {data && (
          <div className="max-h-[60vh] space-y-4 overflow-y-auto">
            {Object.keys(data.loads).length > 0 && (
              <div>
                <div className="eyebrow mb-1">Top set over time (kg)</div>
                {Object.entries(data.loads).map(([n, pts]) => (
                  <div key={n} className="flex gap-2 text-xs"><span className="w-36 truncate text-zinc-400">{n}</span>
                    <span className="text-zinc-200 tabular-nums">{pts.map((p) => p.kg).join(" → ")}</span></div>
                ))}
              </div>
            )}
            <table className="w-full text-xs">
              <tbody>
                {data.sessions.filter((s) => s.date <= today || s.done).map((s) => (
                  <tr key={s.ref} className="border-b border-[#161616] align-top">
                    <td className="py-2 pr-3 text-zinc-500 whitespace-nowrap">{s.date}</td>
                    <td className="py-2 pr-3 text-zinc-200">{s.title}</td>
                    <td className="py-2 pr-3">{s.done ? <span className="text-emerald-300">Done</span> : s.date === today ? <span className="text-zinc-400">Today</span> : <span className="text-red-300">Missed</span>}</td>
                    <td className="py-2 text-zinc-400">
                      {s.prs.length > 0 && <div className="text-amber-300">PR: {s.prs.map((p) => `${p.name} ${p.weight_kg ? `${p.weight_kg} kg` : `${p.reps} reps`}`).join(", ")}</div>}
                      {s.notes && <div className="italic">"{s.notes}"</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
